import { CancelablePromise as Promise } from "../utils/cancelablePromise";
import { Dialog, IDialogButton } from "./framework/dialog";
import Api from "../services/api";
import Translator from "../utils/translator";
import { UserService } from "../utils/users";
import { dispatcher, DispatcherEvents } from "../utils/dispatcher";
import { DriveStore } from "../store/reducers";
import { IApiError } from "../utils/api.interfaces";

interface IEula {
    show(user: any): Promise;
}

interface IEulaFile {
    path: string;
    jurisdiction: string;
    language: string;
    version: number;
}

const FORCE_TO_SCROLL_VENDORS = [] as string[];

const filterAvailableEula = (eula: IEulaFile[], jurisdiction: string) => {
    let jurisdictionEulas = eula.filter(f => f.jurisdiction === jurisdiction);

    if (jurisdictionEulas.length > 0) {
        return jurisdictionEulas;
    }

    return eula.filter(f => !f.jurisdiction);
};

const languagePriorities = [
    "en",
    "fr",
    "fr-ca",
    "fr-fr"
];

const sortLanguages = (langA: string, langB: string) => {
    let priorityA = languagePriorities.indexOf(langA.toLowerCase());
    priorityA = priorityA === -1 ? 100 : priorityA;
    let priorityB = languagePriorities.indexOf(langB.toLowerCase());
    priorityB = priorityB === -1 ? 100 : priorityB;

    if (priorityA !== priorityB) {
        return priorityA - priorityB;
    }
    return langA < langB ? -1 : 1;
};

export const renderEulaLanguageSelector = (languages: string[], selectedLanguage: string): string | undefined => {
    let sortedLanguages = [...languages].sort(sortLanguages);

    if (sortedLanguages.length <= 1) {
        return undefined;
    }

    let options = sortedLanguages.map(language => {
        let selected = language === selectedLanguage ? " selected" : "";
        return `<option class="noTranslate" value="${ language }"${ selected }>${ language.toUpperCase() }</option>`;
    }).join("");

    return `<div id="eula-lang-wrapper"><select id="eula-language-selector" aria-label="Language">${ options }</select></div>`;
};

export const getEulaTranslationLanguage = (language: string): string => {
    let normalizedLanguage = language.toLowerCase();
    if (normalizedLanguage === "fr-ca") {
        return "fr";
    }
    if (normalizedLanguage === "zh") {
        return "zh-Hans";
    }
    return language;
};

export const setEulaDialogLanguage = (translator: Pick<Translator, "language" | "validateCache">, language: string): string => {
    let translationLanguage = getEulaTranslationLanguage(language);
    translator.language = translationLanguage;
    translator.validateCache(translationLanguage);
    return translationLanguage;
};

export const updateEulaLanguageSelector = (selector: HTMLSelectElement, language: string, isLoading = false): void => {
    selector.value = language;
    selector.disabled = isLoading;
};

export default function Eula(
    store: DriveStore,
    translator: Translator,
    userService: UserService,
    dbJurisdiction: string,
    api: Api,
    eulas: string[]
): IEula {
    let alreadyBeenScrolled = false;
    let currentLang: string;
    let parsedEulasHash = filterAvailableEula(eulas.map(path => {
        let name = path.split("\\")[1];
        let [, language, version, jur] = name.split("_");
        let jurisdiction = (jur || "").split(".")[0].toLowerCase();

        return {
            path,
            language,
            jurisdiction,
            version: parseInt(version)
        };
    }), dbJurisdiction.toLowerCase()).reduce((acc, file) => {
        acc[file.language] = file;
        return acc;
    }, {} as { [language: string]: IEulaFile });
    let highestVersion = (function() {
        let version = 1;

        for (let lang in parsedEulasHash) {
            version = version < parsedEulasHash[lang].version ? parsedEulasHash[lang].version : version;
        }

        return version;
    })();
    let addTargetBlank = (html: string): string => {
        // Parse HTML string into HTML document
        let link;
        let parser = new DOMParser();
        let serializer = new XMLSerializer();
        let doc = parser.parseFromString(html, "text/html");
        let linkElements = doc.getElementsByTagName("a");

        // Add target "_blank" to all links
        for (let i = 0; i < linkElements.length; i++) {
            link = linkElements[i];
            link.target = "_blank";
        }

        // Make it a string again
        return serializer.serializeToString(doc);
    };
    let getBody = (html: string): string => {
        let htmlWithFixedLinks = addTargetBlank(html);
        let bodyContent;
        let pageContentExtractor = /<body[^>]*>([\s\S]*)<\/body[\s]*>/im;

        pageContentExtractor["lastIndex"] = 0;
        bodyContent = pageContentExtractor.exec(htmlWithFixedLinks);

        return bodyContent ? bodyContent[1] : htmlWithFixedLinks;
    };

    let getTranslatedEulaContent = (body: any, acceptOnlyEula: boolean): Promise<string> => {
        if (acceptOnlyEula) {
            const link = "https://www.geotab.com/blog/eua-changes/";
            const message = [
                "Unless Geotab has otherwise explicitly agreed in writing, the End User Agreement set out in this notice shall apply to your use of the Geotab solution from and after December 1, 2023.",
                "For more information about the changes made to our End User Agreement, please visit: {link}."
            ];
            return translator.translate(message).then(translatedMessageStrings => {
                let translatedMessage = translatedMessageStrings.join(" ").replace("{link}", `<a href="${ link }" target="_blank">${ link }</a>`);
                return `<div class="eula-content noTranslate">
                            <div class="eula-message">${ translatedMessage }</div>
                            ${ body }
                        </div>`;
            });
        } else {
            return Promise.resolve(`<div class="eula-content noTranslate">${ body }</div>`);
        }
    };

    let getEulaHtml = (file, language, isAcceptOnly) => {
        return new Promise((resolve, reject) => {
            geotab.load(file, content => {
                if (content) {
                    resolve(getBody(content));
                    return;
                }

                reject(new Error(`Could not find file: eula_${ language }`));
            }, () => {
                reject(new Error(`Could not find file: eula_${ language }`));
            });
        }).then(eulaBody => getTranslatedEulaContent(eulaBody, isAcceptOnly));
    };

    let needToScrollToTheEnd = () => {
        let vendor = window.whitelabel;
        return vendor && FORCE_TO_SCROLL_VENDORS.indexOf(vendor) > -1;
    };

    let checkBoxesAndScrollAreChecked = (eulaContent: HTMLElement): boolean => {
        let checkBoxes = eulaContent.querySelectorAll(`input[type="checkbox"]`);
        let uncheckedBoxes = Array.prototype.filter.call(checkBoxes, chbx => chbx.checked === false);
        let isEulaBiggerThanWindow = !!(eulaContent.scrollHeight >= eulaContent.offsetHeight);

        if (isEulaBiggerThanWindow) {
            let isFullyScrolled = alreadyBeenScrolled || eulaContent.scrollHeight - (eulaContent.scrollTop + window.innerHeight) <= 0;

            if (isFullyScrolled) {
                alreadyBeenScrolled = true;
            }

            return !uncheckedBoxes.length && isFullyScrolled;
        } else {
            return !uncheckedBoxes.length;
        }
    };

    // toggle disabled attr of accept button
    let resetAcceptButtonStatus = (isAcceptOnly: boolean): void => {
        let innerBlock = document.getElementById("eulaDialog-inner") as HTMLElement;

        if (innerBlock) {
            let eulaContent = innerBlock.parentElement as HTMLElement;

            if (!needToScrollToTheEnd()) {
                let checkBoxes = eulaContent.querySelectorAll(`input[type="checkbox"]`);
                [].forEach.call(checkBoxes, (checkbox: HTMLInputElement) => {
                    checkbox.checked = true;
                    checkbox.disabled = true;
                });
            } else {
                let isScrolledAndChecked = checkBoxesAndScrollAreChecked(eulaContent);
                let acceptButton = (isAcceptOnly ? document.getElementById("eula_ok") : document.getElementById("eula_accept")) as HTMLInputElement;
                let declineButton = isAcceptOnly ? undefined : document.getElementById("eula_decline");
                if (isScrolledAndChecked) {
                    acceptButton.disabled = false;
                    declineButton?.classList.add("button_secondary");
                } else {
                    acceptButton.disabled = true;
                    declineButton?.classList.remove("button_secondary");
                }
            }
        }
    };

    let isEulaAccepted = (user: any) => user.acceptedEULA === highestVersion;

    let getEulaByLang = (lang: string) => {
        let eulaLanguage = lang;
        if (!parsedEulasHash.hasOwnProperty(eulaLanguage)) {
            if (eulaLanguage === "fr" && parsedEulasHash.hasOwnProperty("fr-CA")) {
                eulaLanguage = "fr-CA";
            } else if (eulaLanguage === "zh-Hans" && parsedEulasHash.hasOwnProperty("zh")) {
                eulaLanguage = "zh";
            }
        }
        return parsedEulasHash[eulaLanguage] || parsedEulasHash["en"] || parsedEulasHash[Object.keys(parsedEulasHash)[0]];
    };

    let parsePath = (path: string) => `/brands/${ path }`;

    let renderLanguageSelector = (selectedLanguage: string) => renderEulaLanguageSelector(Object.keys(parsedEulasHash), selectedLanguage);

    return {
        show: (user: any) => {
            let eulaDialog: Dialog;
            let eula = getEulaByLang(user.language);
            let file = parsePath(eula.path);
            let restoreUserLanguage = () => {
                translator.language = user.language;
                translator.validateCache(user.language);
            };
            const isAcceptOnly: boolean = !window.whitelabel && user.acceptedEULA && user.acceptedEULA > 0 && highestVersion === 20;
            let eulaPromise = new Promise((resolve, reject) => {
                let rejectWithRestoredUserLanguage = (error: Error | IApiError) => {
                    restoreUserLanguage();
                    reject(error);
                };
                if (isEulaAccepted(user)) {
                    resolve(true);
                } else {
                    setEulaDialogLanguage(translator, eula.language);
                    getEulaHtml(file, eula.language, isAcceptOnly) // getting eula content
                    .then((body) => {
                        let dialogButtons: IDialogButton[] = [];
                        if (!isAcceptOnly) {
                            dialogButtons.push({
                                id: "eula_decline",
                                text: "Decline",
                                className: "button_secondary",
                                callback: () => {
                                    restoreUserLanguage();
                                    eulaDialog.remove();
                                    resolve(false);
                                }
                            });
                        }
                        dialogButtons.push({
                            id: isAcceptOnly ? "eula_ok" : "eula_accept",
                            text: isAcceptOnly ? "OK" : "Accept",
                            callback: () => {
                                api.call("AcceptEULA", { version: eula.version }, changedUser => {
                                    restoreUserLanguage();
                                    userService.setUser(changedUser);
                                    eulaDialog.remove();
                                    resolve(true);
                                }, rejectWithRestoredUserLanguage);
                            }
                        });

                        eulaDialog = new Dialog("eulaDialog", body, translator, renderLanguageSelector(eula.language), dialogButtons, {
                            closable: false,
                            expandable: !needToScrollToTheEnd(),
                            showCloseIcon: false,
                            buttonsText: needToScrollToTheEnd() || isAcceptOnly ? undefined : "By tapping Accept, you confirm that you have read and agree to the terms.",
                            isNightMode: store.getState().data.nightMode
                        });
                        eulaDialog.open().then(() => {
                            if (eulaDialog.el) {
                                resetAcceptButtonStatus(isAcceptOnly);

                                // adding event handlers
                                let checkboxes = eulaDialog.el.querySelectorAll(`input[type="checkbox"]`);

                                Array.prototype.forEach.call(checkboxes, chbx => {
                                    chbx.addEventListener("change", resetAcceptButtonStatus, false);
                                });

                                let languageSelect: HTMLSelectElement | null;
                                let languageChangeHandler: (event: Event) => void;
                                let isLanguageChangePending = false;
                                let bindLanguageSelector = () => {
                                    let nextLanguageSelect = document.getElementById("eula-language-selector") as HTMLSelectElement | null;
                                    if (languageSelect === nextLanguageSelect) {
                                        if (languageSelect) {
                                            updateEulaLanguageSelector(languageSelect, currentLang, isLanguageChangePending);
                                        }
                                        return;
                                    }

                                    languageSelect?.removeEventListener("change", languageChangeHandler);
                                    languageSelect = nextLanguageSelect;
                                    if (languageSelect) {
                                        updateEulaLanguageSelector(languageSelect, currentLang, isLanguageChangePending);
                                        languageSelect.addEventListener("change", languageChangeHandler);
                                    }
                                };
                                languageChangeHandler = (event: Event) => {
                                    let select = event.currentTarget as HTMLSelectElement;
                                    let lang = select.value;

                                    if (isLanguageChangePending || lang === currentLang) {
                                        updateEulaLanguageSelector(select, currentLang, isLanguageChangePending);
                                        return;
                                    }

                                    dispatcher.trigger(DispatcherEvents.DriveAppInteraction, event);
                                    let translatedEula = getEulaByLang(lang);
                                    let translatedFile = parsePath(translatedEula.path);

                                    currentLang = lang;
                                    isLanguageChangePending = true;
                                    let translationLanguage = setEulaDialogLanguage(translator, lang);
                                    updateEulaLanguageSelector(select, currentLang, isLanguageChangePending);

                                    let dialogLanguagePromise = eulaDialog.setLanguage(translationLanguage).then(() => bindLanguageSelector());
                                    Promise.all([getEulaHtml(translatedFile, lang, isAcceptOnly), dialogLanguagePromise])
                                        .then(([translatedBody]) => {
                                            eulaDialog.setMessage(translatedBody);
                                            isLanguageChangePending = false;
                                            bindLanguageSelector();
                                        })
                                        .catch(error => {
                                            isLanguageChangePending = false;
                                            bindLanguageSelector();
                                            rejectWithRestoredUserLanguage(error);
                                        });
                                };
                                bindLanguageSelector();

                                ((document.getElementById("eulaDialog-inner") as HTMLElement).parentElement as HTMLElement)
                                .addEventListener("scroll", () => resetAcceptButtonStatus(isAcceptOnly), false);
                            }
                        }).catch(rejectWithRestoredUserLanguage);
                    }).catch(rejectWithRestoredUserLanguage);
                }
            });

            currentLang = eula.language;
            return eulaPromise;
        }
    };
}